import React, { useState, useEffect, useRef } from 'react';
import { Toaster } from 'react-hot-toast';

import Layout from './components/layout/Layout';
import { MontosProvider } from './context/MontosContext';
import { BadgesProvider } from './context/BadgesContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import LoginPage from './components/auth/LoginPage';

// Caja
import DashboardCaja from './components/DashboardCaja';
// Operaciones
import ServicioManager     from './components/servicio/ServicioManager';
import VentaManager        from './components/venta/VentaManager';
import PresupuestosManager from './components/PresupuestosManager';
import TrabajosManager     from './components/trabajos/TrabajosManager';

// Admin
import ClienteManager     from './components/cliente/ClienteManager';
import GestorProductos    from './components/productos/GestorProductos';
import RadarMantenimiento from './components/RadarMantenimiento';
import DashboardFinanzas  from './components/finanzas/DashboardFinanzas';
import UsuariosManager    from './components/usuarios/UsuariosManager';
import MisOrdenes         from './components/ordenes/MisOrdenes';
import MiAgenda           from './components/ordenes/MiAgenda';
import MiSueldo           from './components/finanzas/MiSueldo';
import LoMio             from './components/propio/LoMio';
import MiEspacio          from './components/miespacio/MiEspacio';
import Configuracion      from './components/configuracion/Configuracion';
import PortalEmpresa      from './components/empresa/PortalEmpresa';
import MapaManager from './components/mapa/MapaManager';
import api from './services/api';
import { setColoresTecnicos } from './utils/estados';
import ElegirNavegacion from './components/ui/ElegirNavegacion';

function AppInterna() {
    const { autenticado, esAdmin, usuario } = useAuth();
    const [seccionActual, setSeccionActual] = useState(esAdmin ? 'caja' : 'mis-ordenes');
    const [clientePreload, setClientePreload] = useState(null);
    const [presupuestoOrigen, setPresupuestoOrigen] = useState(null);
    const [ordenOrigen, setOrdenOrigen] = useState(null);
    const [abrirCrear, setAbrirCrear] = useState(false);
    const [modoInicialServicio, setModoInicialServicio] = useState(null);
    // Trabajos: abrir directo el formulario de alta ('nuevo' | 'venta')
    const [trabajoNuevo, setTrabajoNuevo] = useState(null);
    const [, setColoresVer] = useState(0);

    // Bug: como AppInterna nunca se desmonta al cerrar sesión (solo cambia
    // qué devuelve el render), seccionActual quedaba con el valor de la
    // sesión anterior — al volver a entrar aparecía la última pantalla en
    // vez del dashboard inicial. Se resetea a mano cada vez que autenticado
    // pasa de false a true (un login real, no cada render).
    const estabaAutenticado = useRef(autenticado);
    useEffect(() => {
        if (!estabaAutenticado.current && autenticado) {
            setSeccionActual(esAdmin ? 'caja' : 'mis-ordenes');
        }
        estabaAutenticado.current = autenticado;
    }, [autenticado, esAdmin]);

    // Colores de los técnicos (elegidos en Usuarios o automáticos): se cargan al
    // entrar y la app se vuelve a dibujar con ellos.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
        if (!autenticado || usuario?.rol === 'EMPRESA') return;
        api.get('/ordenes/tecnicos')
            .then(r => { setColoresTecnicos(Array.isArray(r.data) ? r.data : []); setColoresVer(v => v + 1); })
            .catch(() => {});
    }, [autenticado, usuario?.rol]);

    if (!autenticado) return <LoginPage />;
    // Portal Empresa (7-oct-2026): la empresa ve solo su portal, nada de la app interna
    if (usuario?.rol === 'EMPRESA') return <PortalEmpresa />;

    const irASeccionConCliente = (seccion, cliente) => {
        setClientePreload(cliente);
        setSeccionActual(seccion);
    };

    const ejecutarPresupuesto = (presupuesto) => {
        setPresupuestoOrigen(presupuesto);
        setSeccionActual('servicio-tecnico');
    };

    const ejecutarOrden = (orden) => {
        setOrdenOrigen(orden);
        setSeccionActual('servicio-tecnico');
    };

    // El técnico solo tiene "Hoy", su agenda y "Mi mes" (4-oct-2026). Cualquier
    // otra sección (por un link viejo, una notificación) lo lleva a "Hoy".
    const SECCIONES_TECNICO = ['mis-ordenes', 'mi-agenda', 'mi-sueldo', 'lo-mio'];
    const renderSeccion = () => {
        const seccion = esAdmin || SECCIONES_TECNICO.includes(seccionActual) ? seccionActual : 'mis-ordenes';
        switch (seccion) {
            case 'caja':
                return <DashboardCaja setVistaActual={(seccion, opts) => {
                    if (opts?.crear && seccion === 'trabajos') setTrabajoNuevo(opts.crear === true ? 'nuevo' : opts.crear);
                    else if (opts?.crear) setAbrirCrear(true);
                    if (opts?.modo) setModoInicialServicio(opts.modo);
                    setSeccionActual(seccion);
                }} />;
            case 'venta':
                return <VentaManager clienteInicial={clientePreload} onClienteConsumido={() => setClientePreload(null)}
                    abrirCrearDirecto={abrirCrear} onCrearConsumido={() => setAbrirCrear(false)} />;
            case 'pedidos':   // la bandeja de Pedidos ahora es parte de Trabajos (20-oct-2026)
            case 'trabajos':
                return esAdmin ? <TrabajosManager nuevoInicial={trabajoNuevo} clienteInicial={clientePreload} onIrInicio={() => setSeccionActual('caja')}
                    onInicialConsumido={() => { setTrabajoNuevo(null); setClientePreload(null); }} /> : null;
            case 'servicio-tecnico':
                return <ServicioManager
                    clienteInicial={clientePreload}
                    onClienteConsumido={() => setClientePreload(null)}
                    presupuestoOrigen={presupuestoOrigen}
                    onPresupuestoOrigenConsumido={() => setPresupuestoOrigen(null)}
                    ordenOrigen={ordenOrigen}
                    onOrdenOrigenConsumido={() => setOrdenOrigen(null)}
                    abrirCrearDirecto={abrirCrear}
                    onCrearConsumido={() => setAbrirCrear(false)}
                    modoInicial={modoInicialServicio}
                    onModoInicialConsumido={() => setModoInicialServicio(null)}
                />;
            case 'presupuestos':
                return <PresupuestosManager />;
            // 28-oct-2026: Rentabilidad ahora está en la ficha de cada cliente con tarifa (Clientes)
            case 'rentabilidad':
            case 'clientes':
                return <ClienteManager
                    abrirCrearDirecto={abrirCrear} onCrearConsumido={() => setAbrirCrear(false)}
                    onNuevoServicio={(c) => {
                        // Admin: el alta va a Trabajos (Servicio Técnico ya no está en el menú)
                        if (esAdmin) { setTrabajoNuevo('nuevo'); irASeccionConCliente('trabajos', c); }
                        else irASeccionConCliente('servicio-tecnico', c);
                    }}
                    onNuevaVenta={(c) => irASeccionConCliente('venta', c)}
                />;
            case 'productos':
                return <GestorProductos />;
            case 'radar':
                return <RadarMantenimiento onCrearTrabajo={esAdmin ? (c) => { setTrabajoNuevo('nuevo'); irASeccionConCliente('trabajos', c); } : null} />;
            case 'finanzas':
                return <DashboardFinanzas />;
            case 'usuarios':
                return <UsuariosManager />;
            case 'mis-ordenes':
                return <MisOrdenes tecnicoId={usuario?.id} onEjecutarOrden={ejecutarOrden} />;
            case 'mi-agenda':
                return <MiAgenda tecnicoId={usuario?.id} />;
            case 'mi-sueldo':
                return <MiSueldo />;
            case 'lo-mio':
                return <LoMio />;
            case 'mi-espacio':
                return <MiEspacio />;
            case 'mapa':
                return esAdmin ? <MapaManager
                    onCrearTrabajo={(c) => { setTrabajoNuevo('nuevo'); irASeccionConCliente('trabajos', c); }}
                    onAbrirPedido={(id) => { try { sessionStorage.setItem('abrirPedido', String(id)); } catch { /* */ } setSeccionActual('trabajos'); }} /> : null;
            case 'configuracion':
                return esAdmin ? <Configuracion /> : null;
            default:
                return (
                    <div className="flex items-center justify-center h-64 text-muted font-black text-sm uppercase tracking-widest">
                        Sección en construcción
                    </div>
                );
        }
    };

    return (
        <MontosProvider>
            <BadgesProvider>
                <Layout vistaActual={seccionActual} setVistaActual={setSeccionActual}>
                    {renderSeccion()}
                </Layout>
            </BadgesProvider>
        </MontosProvider>
    );
}

export default function App() {
    return (
        <AuthProvider>
            <Toaster
                position="top-right"
                toastOptions={{
                    duration: 3000,
                    // 29-oct-2026: colores del tema (en modo oscuro el aviso salía blanco con letra clara)
                    style: { fontSize: '1.1em', fontWeight: 'bold', background: 'var(--color-bg-card)', color: 'var(--color-ink)',
                        border: '1px solid var(--color-line)', boxShadow: '0 8px 24px rgba(0,0,0,0.35)', maxWidth: 'min(420px, calc(100vw - 32px))' }
                }}
            />
            <AppInterna />
            <ElegirNavegacion />
        </AuthProvider>
    );
}